import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileStack, Zap, Clock, UserRound, ShieldCheck, Gauge, History, ChevronRight, RotateCcw, Trash2 } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { adminApi } from "../lib/api";
import { useAuth } from "../lib/auth";
import AuditEntryRow from "../components/AuditEntryRow";

function StatCard({ icon: Icon, label, value, testid, subtle }) {
  return (
    <div
      className="bg-white border border-primary/15 rounded-sm p-5 hover:-translate-y-0.5 hover:shadow-md transition-all group"
      data-testid={testid}
    >
      <div className="flex items-center justify-between mb-4">
        <div className="w-9 h-9 bg-primary/10 text-primary rounded-sm flex items-center justify-center group-hover:bg-primary group-hover:text-white transition-colors">
          <Icon size={16} strokeWidth={1.8} />
        </div>
        {subtle && (
          <span className="font-mono text-[10px] text-foreground/40 tracking-wider">
            {subtle}
          </span>
        )}
      </div>
      <div className="font-display text-3xl font-semibold text-primary leading-none">
        {value}
      </div>
      <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-foreground/60 mt-2">
        {label}
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { t } = useI18n();
  const { can } = useAuth();
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [audit, setAudit] = useState(null);
  const [error, setError] = useState("");
  const [resetState, setResetState] = useState("idle"); // idle | loading | success | error

  const handleResetToday = async () => {
    if (!window.confirm("Réinitialiser les quotas du jour ? Les utilisateurs bloqués pourront à nouveau poser des questions.")) return;
    setResetState("loading");
    try {
      const { data } = await adminApi.resetTodayQuotas();
      setResetState("success");
      setTimeout(() => setResetState("idle"), 3000);
      console.info("Quotas reset:", data);
    } catch (e) {
      setResetState("error");
      setTimeout(() => setResetState("idle"), 3000);
    }
  };

  useEffect(() => {
    Promise.all([adminApi.stats(), adminApi.logs(10)])
      .then(([s, l]) => {
        setStats(s.data);
        setLogs(l.data.items || []);
      })
      .catch((e) => setError(e?.response?.data?.detail || "Error"));
    if (can && can("audit.read")) {
      adminApi.auditSummary(8).then(({ data }) => setAudit(data)).catch(() => {});
    }
  }, [can]);

  return (
    <div data-testid="admin-dashboard">
      <div className="mb-8">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-accent mb-2">
          / {t("admin.overview")}
        </div>
        <h1 className="font-display text-3xl md:text-4xl font-semibold text-primary tracking-tight">
          {t("admin.dashboard")}
        </h1>
      </div>

      {error && (
        <div className="mb-6 text-sm text-[#991B1B] bg-[#FEE2E2] border border-[#FECACA] px-3 py-2 rounded-sm">
          {error}
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
        <StatCard
          icon={FileStack}
          label={t("admin.statsDocs")}
          value={stats?.documents_total ?? "—"}
          subtle={`+${stats?.documents_en_vigueur ?? 0} actifs`}
          testid="stat-card-docs"
        />
        <StatCard
          icon={Zap}
          label={t("admin.statsChunks")}
          value={stats?.chunks_total ?? "—"}
          testid="stat-card-chunks"
        />
        <StatCard
          icon={UserRound}
          label={t("admin.statsQueries")}
          value={stats?.queries_total ?? 0}
          testid="stat-card-queries"
        />
        <StatCard
          icon={Clock}
          label={t("admin.statsAvgTime")}
          value={stats ? Math.round(stats.avg_response_time_ms || 0) : "—"}
          subtle="ms"
          testid="stat-card-avgtime"
        />
        <StatCard
          icon={ShieldCheck}
          label={t("admin.statsAdmin")}
          value={stats?.queries_admin ?? 0}
          testid="stat-card-admin-q"
        />
        <StatCard
          icon={Gauge}
          label={t("admin.statsUptime")}
          value={`${stats?.uptime_percent ?? 99.9}%`}
          testid="stat-card-uptime"
        />
      </div>

      {/* Quota reset */}
      <section className="mt-8 bg-white border border-primary/15 rounded-sm p-5" data-testid="quota-reset-panel">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-accent mb-1">/ gestion des quotas</div>
            <h2 className="font-display text-lg font-semibold text-primary flex items-center gap-2">
              <RotateCcw size={15} /> Réinitialisation des quotas gratuits
            </h2>
            <p className="text-xs text-foreground/60 mt-1">
              Remet à zéro les compteurs du jour pour tous les utilisateurs non-abonnés.
              Les quotas se réinitialisent aussi automatiquement chaque jour à minuit.
            </p>
          </div>
          <button
            onClick={handleResetToday}
            disabled={resetState === "loading"}
            className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-sm text-sm font-medium transition-colors ${
              resetState === "success"
                ? "bg-green-600 text-white"
                : resetState === "error"
                ? "bg-red-600 text-white"
                : "bg-primary text-white hover:bg-primary-light disabled:opacity-50"
            }`}
            data-testid="btn-reset-quotas"
          >
            <Trash2 size={14} />
            {resetState === "loading"
              ? "Réinitialisation..."
              : resetState === "success"
              ? "✓ Quotas réinitialisés"
              : resetState === "error"
              ? "Erreur — réessayer"
              : "Réinitialiser les quotas du jour"}
          </button>
        </div>
      </section>

      {/* Audit widget (only for users with audit.read) */}
      {audit && (
        <section className="mt-10" data-testid="audit-widget">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-accent mb-1">/ journal d'audit</div>
              <h2 className="font-display text-xl font-semibold text-primary flex items-center gap-2">
                <History size={16} /> Activité admin
              </h2>
              <p className="text-xs text-foreground/60 mt-1">
                {audit.total} événements · {Object.entries(audit.by_entity || {}).map(([k, v]) => `${k}:${v}`).join(" · ") || "aucune action enregistrée"}
              </p>
            </div>
            <Link
              to="/admin/audit"
              className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
              data-testid="audit-widget-see-all"
            >
              Voir tout <ChevronRight size={12} />
            </Link>
          </div>
          <div className="bg-white border border-primary/15 rounded-sm overflow-hidden" data-testid="audit-widget-list">
            {audit.recent.length === 0 && (
              <div className="px-5 py-8 text-center text-sm text-foreground/50">
                Aucune action admin enregistrée pour le moment.
              </div>
            )}
            <ul>
              {audit.recent.map((e) => (
                <AuditEntryRow key={e.id} entry={e} />
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* Recent logs */}
      <section className="mt-10">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-accent mb-1">
              / activité utilisateurs
            </div>
            <h2 className="font-display text-xl font-semibold text-primary">
              {t("admin.recentLogs")}
            </h2>
          </div>
        </div>

        <div className="bg-white border border-primary/15 rounded-sm overflow-hidden" data-testid="recent-logs">
          {logs.length === 0 && (
            <div className="px-5 py-8 text-center text-sm text-foreground/50">
              {t("admin.noLogs")}
            </div>
          )}
          <ul>
            {logs.map((l, i) => (
              <li
                key={l.id || i}
                className="px-5 py-3 border-b border-primary/5 last:border-none hover:bg-primary/5 transition-colors"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <p className="text-sm text-foreground/85 truncate font-medium">
                      {l.question}
                    </p>
                    <div className="flex items-center gap-3 mt-1">
                      <span
                        className={`font-mono text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm ${
                          l.user_type === "admin"
                            ? "bg-primary/10 text-primary"
                            : "bg-accent/20 text-[#b4551c]"
                        }`}
                      >
                        {l.user_type}
                      </span>
                      <span className="font-mono text-[10px] text-foreground/50">
                        {l.language?.toUpperCase()} · {l.response_time_ms} ms
                      </span>
                    </div>
                  </div>
                  <span className="font-mono text-[10px] text-foreground/40 shrink-0">
                    {new Date(l.created_at).toLocaleString()}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>
    </div>
  );
}
